import { useState } from 'react'
import { Box, Typography, IconButton, Paper, Collapse, Tooltip } from '@mui/material'
import BugReportIcon from '@mui/icons-material/BugReport'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import { parseRtmUsername } from '../utils/rtmUsernameBuilder'

/**
 * Debug panel to show RTM data and user information in real-time
 */
const DebugPanel = ({ uidToUsernameMap, remoteUsers, agoraRtcProps, agoraRtmProps, onRequestFocusUid }) => {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState({
    rtm: true,
    users: true,
    map: true,
  })

  const handleFocusUser = (participantId) => {
    if (typeof onRequestFocusUid === 'function') {
      onRequestFocusUid(participantId)
    }
  }

  if (!open) {
    return (
      <IconButton
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex: 9999,
          backgroundColor: '#3B82F6',
          color: 'white',
          '&:hover': {
            backgroundColor: '#2563EB',
          },
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
        }}
      >
        <BugReportIcon />
      </IconButton>
    )
  }

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        width: 400,
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderBottom: '1px solid rgba(59, 130, 246, 0.3)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3B82F6' }}>
            Agora Debug Panel
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {/* RTM Props */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
            onClick={() => setExpanded((prev) => ({ ...prev, rtm: !prev.rtm }))}
          >
            {expanded.rtm ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="subtitle2" sx={{ color: '#10B981', fontWeight: 600 }}>
              RTM Props (Your Side)
            </Typography>
          </Box>
          <Collapse in={expanded.rtm}>
            <Box sx={{ pl: 2, backgroundColor: 'rgba(16, 185, 129, 0.05)', p: 1, borderRadius: 1 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(
                  {
                    username: agoraRtmProps?.username || 'N/A',
                    displayUsername: agoraRtmProps?.displayUsername,
                    hasToken: !!agoraRtmProps?.token,
                  },
                  null,
                  2
                )}
              </pre>
            </Box>
          </Collapse>
        </Box>

        {/* Username Map */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
            onClick={() => setExpanded((prev) => ({ ...prev, map: !prev.map }))}
          >
            {expanded.map ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="subtitle2" sx={{ color: '#F59E0B', fontWeight: 600 }}>
              UID → Username Map
            </Typography>
          </Box>
          <Collapse in={expanded.map}>
            <Box sx={{ pl: 2, backgroundColor: 'rgba(245, 158, 11, 0.05)', p: 1, borderRadius: 1 }}>
              {Object.keys(uidToUsernameMap).length === 0 ? (
                <Typography sx={{ color: '#9CA3AF', fontSize: '11px' }}>No mappings yet</Typography>
              ) : (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(uidToUsernameMap, null, 2)}
                </pre>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Remote Users */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
            onClick={() => setExpanded((prev) => ({ ...prev, users: !prev.users }))}
          >
            {expanded.users ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="subtitle2" sx={{ color: '#8B5CF6', fontWeight: 600 }}>
              Remote Users ({remoteUsers.length})
            </Typography>
          </Box>
          <Collapse in={expanded.users}>
            <Box sx={{ pl: 2 }}>
              {remoteUsers.length === 0 ? (
                <Typography sx={{ color: '#9CA3AF', fontSize: '11px' }}>No remote users</Typography>
              ) : (
                remoteUsers.map((user, index) => (
                  <Box
                    key={user.uid}
                    sx={{
                      mb: 1,
                      p: 1,
                      backgroundColor: 'rgba(139, 92, 246, 0.05)',
                      borderRadius: 1,
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: '#8B5CF6', fontWeight: 600, mb: 0.5 }}>
                        User {index + 1}
                      </Typography>
                    <pre style={{ margin: 0, fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {JSON.stringify(
                        (() => {
                          const rawUsername = uidToUsernameMap[user.uid]
                          const parsed = parseRtmUsername(rawUsername)
                          const hasRtmData = !!rawUsername
                          
                          return {
                            participantId: user.participantId || null,
                            uid: user.uid,
                            username: hasRtmData 
                              ? parsed.name 
                              : '(Pending RTM data...)',
                            rawUsername: hasRtmData ? rawUsername : null,
                            role: parsed.role || null,
                            profilePicId: parsed.profilePicId || null,
                            hasAudio: user.hasAudio,
                            hasVideo: user.hasVideo,
                            rtmDataReceived: hasRtmData,
                          }
                        })(),
                        null,
                        2
                      )}
                    </pre>
                    </Box>
                    <Tooltip title="Focus on this user's video" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleFocusUser(user.participantId || user.uid)}
                        sx={{
                          backgroundColor: 'rgba(103, 121, 227, 0.2)',
                          color: '#6779e3',
                          border: '1px solid rgba(103, 121, 227, 0.4)',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 121, 227, 0.3)',
                            borderColor: 'rgba(103, 121, 227, 0.6)',
                          },
                        }}
                      >
                        <CenterFocusStrongIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </Box>

        {/* RTC Props Summary */}
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#EC4899', fontWeight: 600, mb: 1 }}>
            RTC Info
          </Typography>
          <Box sx={{ pl: 2, backgroundColor: 'rgba(236, 72, 153, 0.05)', p: 1, borderRadius: 1 }}>
            <pre style={{ margin: 0, fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(
                {
                  channel: agoraRtcProps?.channel || 'N/A',
                  uid: agoraRtcProps?.uid,
                  maxUid: agoraRtcProps?.maxUid || 'Auto',
                  layout: agoraRtcProps?.layout,
                },
                null,
                2
              )}
            </pre>
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}

export default DebugPanel
